// Third-party
import clsx from 'clsx';
import { includes } from 'lodash';

import { useDMAccess } from '@/components/pages/conversations/SocialInbox/hooks/useDMAccess';
// Hooks
import { useSocialInboxNavigation } from '@/components/pages/conversations/SocialInbox/hooks/useSocialInboxNavigation';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useAcl from '@/utils/hooks/user/useAcl';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import useToggle from '@/utils/hooks/utils/useToggle';

import type { SocialInboxType } from '@/components/pages/conversations/SocialInbox/types';

import { aclFeatures } from '@/lib/acl/aclFeatures';
import { INSTAGRAM } from '@/lib/socialProfile';
// Types/Libs
import {
    DEFAULT_SOCIAL_INBOX_TYPES,
    INSTAGRAM_ONLY_FEATURES,
    SOCIAL_INBOX_MANAGING_OPTIONS
} from '@/components/pages/conversations/SocialInbox/const';

// Components
import Tooltip from '@/components/base/Tooltip';
import { UpsellCrownIcon } from '@/components/composite/Upsell/UpsellCrownIcon/UpsellCrownIcon';
import {
    SocialInboxTitle,
    SocialInboxTypeTitle
} from '@/components/pages/conversations/SocialInbox';
import { DirectMessagesUpsellModal } from '@/components/pages/conversations/SocialInbox/components/DirectMessagesUpsellModal';
import { MentionsUpsellModal } from '@/components/pages/conversations/SocialInbox/components/MentionsUpsellModal';

interface SocialInboxMenuDesktopProps {
    className?: string;
}

export const SocialInboxMenuDesktop = ({ className }: SocialInboxMenuDesktopProps) => {
    const { t } = useTranslation('conversations');

    const { activeSocialProfile } = useActiveSocialProfile();
    const { currentSocialInbox, navigateTo } = useSocialInboxNavigation();
    const { hasAcl } = useAcl();
    const { isOpen: isDMUpsellModalOpen, toggle: toggleDMUpsellModal } = useToggle();
    const { isOpen: isMentionsUpsellModalOpen, toggle: toggleMentionsUpsellModal } = useToggle();

    const hasDirectMessagesAccess = hasAcl(aclFeatures.direct_messages.base.read);
    const hasMentionsAccess = hasAcl(aclFeatures.analytic.mentions.read);
    const hasDMAccess = useDMAccess();

    const renderMenuItem = (socialInboxType: SocialInboxType) => {
        const isInstagram = activeSocialProfile?.social_network_type === INSTAGRAM;
        const isInstagramOnlyFeature = includes(INSTAGRAM_ONLY_FEATURES, socialInboxType);
        const isDirectMessages = socialInboxType === 'dm';
        const isMentions = socialInboxType === 'mention';

        const disabled =
            (isInstagramOnlyFeature && !isInstagram) ||
            (isDirectMessages && !hasDMAccess) ||
            (isMentions && !hasMentionsAccess);

        const handleClick = () => {
            if (isDirectMessages && !hasDirectMessagesAccess) {
                toggleDMUpsellModal();
            } else if (isMentions && !hasMentionsAccess) {
                toggleMentionsUpsellModal();
            } else {
                navigateTo(socialInboxType);
            }
        };

        const showUpsellCrown =
            (isDirectMessages && !hasDirectMessagesAccess) || (isMentions && !hasMentionsAccess);

        return (
            <Tooltip
                content={t('social_inbox.not_yet_available')}
                interactive={true}
                appendTo={() => document.body}
                placement="right"
                disabled={!disabled}>
                <span className="z-20 flex items-center">
                    <button
                        key={socialInboxType}
                        onClick={handleClick}
                        disabled={disabled}
                        className={clsx(
                            'flex w-full items-center gap-2 space-x-2 overflow-hidden rounded-md p-2',
                            {
                                'bg-icoBlue-100 text-icoBlue dark:bg-icoBlue/20 dark:text-icoBlue':
                                    currentSocialInbox === socialInboxType,
                                'text-icoGray-500 hover:bg-icoBlue-100 hover:text-icoBlue dark:text-icoDarkMode-wolf dark:hover:bg-icoBlue/20 dark:hover:text-icoBlue':
                                    currentSocialInbox !== socialInboxType,
                                'cursor-not-allowed opacity-50': disabled
                            }
                        )}>
                        <SocialInboxTypeTitle
                            type={socialInboxType}
                            selected={currentSocialInbox === socialInboxType}
                        />

                        {showUpsellCrown && <UpsellCrownIcon />}
                    </button>
                </span>
            </Tooltip>
        );
    };

    const renderMenuContainer = (title: string, items: readonly SocialInboxType[]) => {
        return (
            <div className="flex flex-col space-y-2">
                <span className="font-GSemiBold text-xs text-icoBlue dark:text-icoDarkMode-blueStats">
                    {title}
                </span>
                {items.map(renderMenuItem)}
            </div>
        );
    };

    return (
        <div
            className={`flex h-full flex-col space-y-8 bg-white dark:bg-icoDarkMode-deepBlack ${className}`}>
            <SocialInboxTitle />

            {renderMenuContainer(t('social_inbox.inboxes'), DEFAULT_SOCIAL_INBOX_TYPES)}

            {renderMenuContainer(t('social_inbox.managing'), SOCIAL_INBOX_MANAGING_OPTIONS)}

            <DirectMessagesUpsellModal isOpen={isDMUpsellModalOpen} toggle={toggleDMUpsellModal} />
            <MentionsUpsellModal
                isOpen={isMentionsUpsellModalOpen}
                toggle={toggleMentionsUpsellModal}
            />
        </div>
    );
};
